import './NewPost.sass';
import 'react-quill/dist/quill.bubble.css';
import './QlEditor.sass';

import React, {
  ClipboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useRoute } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';

import {
  BottomBar,
  Button,
  FormItem,
  Header,
  InfoBlock,
  List,
  ListItem,
  LottieIcon,
  Placeholder,
  ScrollView,
  Select,
  Switch,
} from '$uikit';
import { createPostFormSelector, postsActions } from '$store/posts';
import { RootState } from '$store/rootReducer';
import {
  IcChart28,
  IcChevron16,
  IcCloud28,
  IcGallery28,
  IcStatusSending16,
  IcVideo28,
  IcWheel28,
} from '$assets';
import { AttachPickerManagerRef } from 'shared/components/AttachPickerManager/AttachPickerManager.interface';
import AttachPickerManager from 'shared/components/AttachPickerManager/AttachPickerManager';
import {
  attachActions,
  attachItemsSelector,
  attachLinksSelector,
  attachPhotosSelector,
  attachPollSelector,
  attachRouletteSelector,
  attachTimerSelector,
} from '$store/attach';
import { mainSelector } from '$store/main';
import {
  CLOUD,
  PERSONAL_SUBSCRIPTION_INFO,
  SUBSCRIPTION_MEDIA,
  VERIFICATION_APPLY,
} from '$shared/constants/pages';
import { useCurrentUserInfo, useTranslator, useWikiLink } from '$hooks';
import { authSelector } from '$store/auth';
import { AuthRequired } from '$core/AuthRequired/AuthRequired';
import { findLinkQuill } from '$shared/components/Linkify/Linkify';
import { ATTACHMENTS_COUNT_LIMIT, classNames, rnbr, statReachGoal } from '$utils';
import { getPostDraft, savePostDraft } from '$utils/postDrafts';
import { PostType, SubscriptionType } from '$store/models';
import { ContentPriorityType } from '$store/main/interface';
import { goTo, showModal } from '$navigation/helper';
import { cloudActions } from '$store/cloud';
import { toastActions } from '$store/toast';
import { Tooltip } from '$shared/components';

export function NewPost({ ownerId }: any) {
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const dispatch = useDispatch();
  const user = useCurrentUserInfo();
  const pickerId = `post_${ownerId}`;
  const form = useSelector((state: RootState) => createPostFormSelector(state, pickerId));
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(0);
  const timer = useSelector((state: RootState) => attachTimerSelector(state, pickerId));
  const { mVerified, decryptKeys, hasPersonalSubscription, contentPriority, hasBot } =
    useSelector(mainSelector);
  const t = useTranslator();
  const { isLogged } = useSelector(authSelector);
  const editor = useRef<ReactQuill>(null);
  const [isFocused, setFocused] = useState(false);
  const [rawText, setRawText] = useState('');
  const link = useSelector((state: RootState) => attachLinksSelector(state, pickerId));
  const attachments = useSelector((state: RootState) =>
    attachItemsSelector(state, pickerId),
  );
  const mediaAttachments = useSelector((state: RootState) =>
    attachPhotosSelector(state, pickerId),
  );
  const poll = useSelector((state: RootState) => attachPollSelector(state, pickerId));
  const roulette = useSelector((state: RootState) =>
    attachRouletteSelector(state, pickerId),
  );
  const { route } = useRoute();
  const wikiLink = useWikiLink('/for-creators/publishing-content/types-of-posts');

  const mediaCount = mediaAttachments.length;
  const attachmentsLimit = ATTACHMENTS_COUNT_LIMIT;
  const formType =
    form.type ||
    (decryptKeys[-SubscriptionType.Media] ? PostType.SUBSCRIPTION : PostType.FREE);

  const canEroticPost =
    user.verified === 2 || contentPriority === ContentPriorityType.ADULT;
  const canPost =
    formType === PostType.FREE ||
    (formType === PostType.SUBSCRIPTION &&
      (decryptKeys[-SubscriptionType.Media] ||
        decryptKeys[-SubscriptionType.Adult] ||
        mVerified)) ||
    (formType === PostType.PRIVATE_SUBSCRIPTION && hasPersonalSubscription);

  const isRepost = useMemo(() => {
    return link && (link.type === 'post' || link.type === 'personal_subscription');
  }, [link]);

  useEffect(() => {
    if (user?.isolated && user?.verified === 2) {
      dispatch(
        postsActions.updateFormType({
          formId: pickerId,
          type: PostType.PRIVATE_SUBSCRIPTION,
        }),
      );
    } else if (route.meta?.options?.formType !== undefined) {
      dispatch(
        postsActions.updateFormType({
          formId: pickerId,
          type: parseInt(route.meta?.options?.formType),
        }),
      );
    } else if (isRepost) {
      dispatch(
        postsActions.updateFormType({
          formId: pickerId,
          type: PostType.FREE,
        }),
      );
    }
  }, [isRepost, ownerId, route]);

  const handleChange = useCallback(
    (newVal) => {
      const contents = editor.current?.getEditor().getContents() ?? '';
      const text = editor.current?.getEditor().getText() || '';
      const serialized = JSON.stringify(contents);
      setRawText(text);
      dispatch(
        postsActions.updateFormText({
          formId: pickerId,
          text: serialized,
        }),
      );
      if (user?.id) {
        savePostDraft(user.id, pickerId, text.trim() ? serialized : '');
      }
      attachManager.current?.findLinks(newVal);
    },
    [dispatch, ownerId, attachManager, user?.id],
  );

  const handleChangeType = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      dispatch(
        postsActions.updateFormType({
          formId: pickerId,
          type: parseInt(e.target.value),
        }),
      );
    },
    [dispatch, ownerId],
  );

  const handlePublish = () => {
    if (attachments.length || rawText.trim()) {
      dispatch(postsActions.publish(ownerId));
    } else {
      dispatch(toastActions.setToastFail(t('im_empty_error')));
    }
  };

  const handleSendToTelegram = useCallback(() => {
    dispatch(
      postsActions.updateFormSendToTelegram({
        formId: pickerId,
        send: !form.sendToTelegram,
      }),
    );
  }, [pickerId, form.sendToTelegram]);

  const handlePhotoChange = useCallback(
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey],
  );

  const handleVideoChange = useCallback(
    ({ target }) => {
      if (!target.files.length) {
        return;
      }

      attachManager.current?.uploadVideo(target.files[0]);
      setVideoInputKey(Date.now());
    },
    [attachManager],
  );

  const handleToggleTimer = useCallback(() => {
    attachManager.current?.toggleTimer();
  }, []);

  const handleOpenVerification = useCallback(() => {
    goTo(VERIFICATION_APPLY);
  }, []);

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const { files } = event.clipboardData;

    if (files[0] && files[0].type.includes('image')) {
      attachManager.current?.uploadImages(files);
    }
  };

  const defaultValue = useMemo(() => {
    const text = form.text || (user?.id ? getPostDraft(user.id, pickerId) : '');
    if (!text) {
      return '';
    }

    if (text.substring(0, 1) === '{') {
      try {
        return JSON.parse(text);
      } catch (e) {}
    }

    return text;
  }, []);

  useEffect(() => {
    const quill = editor.current?.getEditor();
    if (!quill) {
      return;
    }

    const text = quill.getText() || '';
    if (!text.trim()) {
      return;
    }

    setRawText(text);
    if (!form.text) {
      dispatch(
        postsActions.updateFormText({
          formId: pickerId,
          text: JSON.stringify(quill.getContents()),
        }),
      );
    }
  }, []);

  const hashtagsLimit = useMemo(() => {
    const items = findLinkQuill(form.text);
    if (!items) {
      return false;
    }

    let tagsCount = 0;
    for (let item of items) {
      if (item.hashtag) {
        tagsCount++;
      }
    }

    return tagsCount > 5;
  }, [form.text]);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const handleOnClickCheckAttachmentsLimit = (e) => {
    if (mediaCount >= attachmentsLimit) {
      dispatch(
        toastActions.setToastFail(t('new_post_limit_error', { attachmentsLimit })),
      );
      return e.preventDefault();
    }
  };

  const infoText = useMemo(() => {
    if (formType === PostType.FREE) {
      return user.verified === 2 ? t('new_post_info_text_free') : null;
    } else if (formType === PostType.SUBSCRIPTION) {
      if (contentPriority === ContentPriorityType.OTHER) {
        return t('new_post_info_text_media');
      } else if (contentPriority === ContentPriorityType.ADULT) {
        return t('new_post_info_text_adult');
      }
    } else if (formType === PostType.PRIVATE_SUBSCRIPTION) {
      return t('new_post_info_text_sponsors');
    }
  }, [formType, contentPriority, t, user]);

  if (!isLogged) {
    return <AuthRequired />;
  }

  function renderTooltip() {
    return (
      <Tooltip
        iconSize={24}
        title={t('new_post_tooltip_type_header')}
        text={
          <div>
            <span className="NewPost__tooltip__title">
              {t('new_post_tooltip_type_free_header')}{' '}
            </span>
            <span className="NewPost__tooltip__text">
              - {t('new_post_tooltip_type_free_text')}
            </span>
            <br />
            <span className="NewPost__tooltip__title">
              {t('new_post_tooltip_type_erotic_header')}{' '}
            </span>
            <span className="NewPost__tooltip__text">
              - {t('new_post_tooltip_type_erotic_text')}
            </span>
            <br />
            <span className="NewPost__tooltip__title">
              {t('new_post_tooltip_type_sponsor_header')}{' '}
            </span>
            <span className="NewPost__tooltip__text">
              - {t('new_post_tooltip_type_sponsor_text')}
            </span>
          </div>
        }
        action={
          <Button stretched href={wikiLink} iconRight={<IcChevron16 />} target="_blank">
            {t('new_post_tooltip_type_button_more')}
          </Button>
        }
      />
    );
  }

  function renderContent() {
    if (contentPriority === ContentPriorityType.ADULT && !mVerified) {
      return (
        <Placeholder
          centered
          title={t('new_post_verification_title')}
          icon={
            <a href="https://ton.diamonds" target="_blank" rel="noreferrer">
              <LottieIcon name="logo" loop={true} />
            </a>
          }
          iconSize={120}
          actions={
            <Button onClick={handleOpenVerification}>
              {t('new_post_verification_button')}
            </Button>
          }
        >
          {t('new_post_verification_description')}
        </Placeholder>
      );
    }
    // @ts-ignore
    return (
      <>
        <ScrollView>
          <div
            className="NewPost"
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <AttachPickerManager ref={attachManager} pickerId={pickerId} />
            <div className="NewPost__attachments">
              {mVerified && (
                <div className="NewPost__attachments__photo_wrap">
                  <Button
                    size="small"
                    mode="secondary"
                    icon={<IcCloud28 />}
                    onClick={() => {
                      if (mediaCount >= attachmentsLimit) {
                        dispatch(
                          toastActions.setToastFail(
                            t('new_post_limit_error', { attachmentsLimit }),
                          ),
                        );
                        return;
                      }

                      dispatch(
                        cloudActions.setSavedContentActions([
                          // attachActions.pushPhotos({
                          //   pickerId,
                          //   photos: mediaAttachments,
                          // }),
                          attachActions.setTimer({ pickerId, time: timer }),
                          attachActions.setLink({ pickerId, link }),
                          postsActions.updateFormType({
                            formId: pickerId,
                            type: formType,
                          }),
                        ]),
                      );
                      dispatch(
                        cloudActions.setAttachParams({
                          pickerId,
                          routeName: route.name,
                          routeParams: route.params,
                        }),
                      );
                      goTo(
                        CLOUD,
                        {},
                        {
                          limit: attachmentsLimit - mediaCount,
                        },
                      );
                    }}
                  >
                    {t('new_post_attach_cloud')}
                  </Button>
                </div>
              )}
              <div className="NewPost__attachments__photo_wrap">
                <Button mode="secondary" icon={<IcGallery28 />} size="small">
                  {t('new_post_attach_photo')}
                </Button>
                <input
                  key={photoInputKey}
                  onChange={handlePhotoChange}
                  onClick={handleOnClickCheckAttachmentsLimit}
                  multiple
                  accept="image/*"
                  type="file"
                />
              </div>
              <div className="NewPost__attachments__photo_wrap">
                <Button mode="secondary" icon={<IcVideo28 />} size="small">
                  {t('new_post_attach_video')}
                </Button>
                <input
                  key={videoInputKey}
                  onChange={handleVideoChange}
                  onClick={handleOnClickCheckAttachmentsLimit}
                  type="file"
                  accept="video/mp4,video/quicktime"
                />
              </div>
              <Button
                mode={timer ? 'light' : 'secondary'}
                size="small"
                icon={<IcStatusSending16 width={28} height={28} />}
                onClick={handleToggleTimer}
              >
                {t('new_post_attach_timer')}
              </Button>
              {!poll && (
                <Button
                  mode="secondary"
                  size="small"
                  icon={<IcChart28 width={28} height={28} />}
                  onClick={() => showModal('poll')}
                >
                  {t('new_post_attach_poll')}
                </Button>
              )}
              {!roulette && user.verified === 2 && (
                <Button
                  mode="secondary"
                  size="small"
                  icon={<IcWheel28 width={28} height={28} />}
                  onClick={() => {
                    statReachGoal('roulette_create_start');
                    showModal('roulette');
                  }}
                >
                  {t('new_post_attach_roulette')}
                </Button>
              )}
            </div>
            {/* @ts-ignore */}
            <ReactQuill
              id="ql_editor"
              bounds=".NewPost"
              ref={editor}
              className={classNames({
                Editor: true,
                focused: isFocused,
              })}
              theme="bubble"
              placeholder={t('new_post_placeholder')}
              defaultValue={defaultValue}
              formats={['bold', 'italic', 'strike', 'underline', 'link', 'code']}
              onChange={handleChange}
              modules={{
                history: { delay: 1000, maxStack: 100, userOnly: false },
                toolbar: [['bold', 'italic', 'underline', 'strike', 'code']],
              }}
            />
            {user.verified === 2 && hasBot && (
              <FormItem>
                <List>
                  <ListItem
                    after={<Switch checked={form.sendToTelegram} />}
                    onClick={handleSendToTelegram}
                  >
                    {t('new_post_send_to_bot')}
                  </ListItem>
                </List>
              </FormItem>
            )}
            {!user?.isolated && (
              <FormItem
                title={t('new_post_type_title')}
                titleAfter={
                  contentPriority === ContentPriorityType.ADULT &&
                  user.verified === 2 &&
                  renderTooltip()
                }
              >
                <Select onChange={handleChangeType} value={formType} disabled={isRepost}>
                  <option value={PostType.FREE}>{t('new_post_type_value_free')}</option>
                  {canEroticPost && (
                    <option value={PostType.SUBSCRIPTION}>
                      {t(
                        contentPriority === ContentPriorityType.ADULT ||
                          user.verified === 2
                          ? 'new_post_type_value_subscription_adult'
                          : 'new_post_type_value_subscription_media',
                      )}
                    </option>
                  )}
                  {ownerId > 0 ? (
                    <option value={PostType.PRIVATE_SUBSCRIPTION}>
                      {t('new_post_type_value_private_subscription')}
                    </option>
                  ) : null}
                </Select>
              </FormItem>
            )}
            <p className="NewPost__infoText">{rnbr(infoText)}</p>
            {formType === PostType.PRIVATE_SUBSCRIPTION && !canPost && (
              <Link routeName={PERSONAL_SUBSCRIPTION_INFO}>
                <InfoBlock type="warning" arrow>
                  {t('new_post_need_turn_on_sponsors')}
                </InfoBlock>
              </Link>
            )}
            {formType === PostType.SUBSCRIPTION &&
              contentPriority === ContentPriorityType.OTHER &&
              !canPost && (
                <Link routeName={SUBSCRIPTION_MEDIA}>
                  <InfoBlock type="warning" arrow>
                    {t('new_post_need_media_subscription')}
                  </InfoBlock>
                </Link>
              )}
            {formType === PostType.SUBSCRIPTION &&
              contentPriority === ContentPriorityType.ADULT &&
              !mVerified &&
              !canPost && (
                <div style={{ cursor: 'pointer' }} onClick={handleOpenVerification}>
                  <InfoBlock type="warning" arrow>
                    {t('new_post_need_verification')}
                  </InfoBlock>
                </div>
              )}
          </div>
          {hashtagsLimit && (
            <div className="NewPost__hashtags_warning">
              <InfoBlock type="warning">{t('new_post_hashtag_limit')}</InfoBlock>
            </div>
          )}
        </ScrollView>
        <BottomBar fixed>
          <Button disabled={!canPost} stretched onClick={handlePublish}>
            {t('new_post_publish')}
          </Button>
        </BottomBar>
      </>
    );
  }

  return (
    <>
      <Header showCloseButton>{t('new_post_title')}</Header>
      {renderContent()}
    </>
  );
}
